import React, { useState, useEffect } from "react";
import { db, functions } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const BuyScanGunModal = ({ isOpen, onClose, userEmail, userName }) => {
  const [formData, setFormData] = useState({
    quantity: 1,
    deliveryName: userName || "",
    email: userEmail || "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Pricing State
  const [pricingConfig, setPricingConfig] = useState(null);
  const [isPricingLoading, setIsPricingLoading] = useState(true);

  // Fetch Pricing
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const configSnap = await getDoc(doc(db, "AdminConfig", "scanGunPricing"));
        if (configSnap.exists()) {
          setPricingConfig(configSnap.data());
        } else {
          setPricingConfig({
            basePrice: 50,
            taxRate: 8,
            platformFee: 5,
            isPlatformFeeEnabled: true,
          });
        }
      } catch (err) {
        console.error("Failed to load pricing:", err);
      } finally {
        setIsPricingLoading(false);
      }
    };
    if (isOpen) {
      fetchPricing();
    }
  }, [isOpen]);

  // Derived Pricing Calculations
  const quantity = Number(formData.quantity) || 0;
  const basePrice = pricingConfig?.basePrice || 50;
  const subtotal = basePrice * quantity;
  const fee = (pricingConfig?.isPlatformFeeEnabled ? pricingConfig?.platformFee : 0) || 0;
  const tax = (subtotal + fee) * ((pricingConfig?.taxRate || 8) / 100);
  const total = subtotal + fee + tax;

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    let formattedValue = value;
    if (field === "phone") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      formattedValue = digits.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    }
    setFormData((prev) => ({ ...prev, [field]: formattedValue }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const { quantity, deliveryName, email, phone, address, city, state, zip } = formData;

    if (!quantity || quantity < 1) {
      setError("Please enter a valid quantity.");
      return;
    }
    if (!deliveryName || !email || !phone || !address || !city || !state || !zip) {
      setError("Please fill in all delivery details.");
      return;
    }

    setIsLoading(true);
    try {
      // Create Stripe Checkout Session
      const createScanGunCheckoutSession = httpsCallable(functions, "createScanGunCheckoutSession");
      
      const response = await createScanGunCheckoutSession({
        quantity: Number(quantity),
        deliveryDetails: {
          deliveryName,
          email,
          phone,
          address,
          city,
          state,
          zip,
        }
      });

      const { url } = response.data;
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("No checkout URL returned");
      }

    } catch (err) {
      console.error("Checkout Error:", err);
      setError(err.message || "Failed to initialize checkout. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden my-8">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Order Scan Guns</h2>
            <p className="text-sm text-gray-500 mt-1">Request physical scan guns for your branches</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl mb-4"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium">
              Order submitted successfully! Our team will process it shortly.
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Quantity</label>
            <input
              type="number"
              min="1"
              max="100"
              value={formData.quantity}
              onChange={(e) => handleInputChange("quantity", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Delivery Name</label>
              <input
                type="text"
                value={formData.deliveryName}
                onChange={(e) => handleInputChange("deliveryName", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John Doe"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Phone Number</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(555) 555-5555"
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Street Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="123 Main St"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1 col-span-1">
              <label className="text-sm font-medium text-gray-700">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="City"
                required
              />
            </div>
            <div className="space-y-1 col-span-1">
              <label className="text-sm font-medium text-gray-700">State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="State"
                required
              />
            </div>
            <div className="space-y-1 col-span-1">
              <label className="text-sm font-medium text-gray-700">Zip Code</label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => handleInputChange("zip", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Zip"
                required
              />
            </div>
          </div>

          {/* Pricing Breakdown Card */}
          {!isPricingLoading && pricingConfig && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2 mt-4 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Scan Gun ({quantity}x ${basePrice.toFixed(2)})</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {pricingConfig.isPlatformFeeEnabled && (
                <div className="flex justify-between text-gray-600">
                  <span>Platform Fee</span>
                  <span>${fee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Estimated Tax ({pricingConfig.taxRate}%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="border-t border-blue-200 pt-2 flex justify-between font-bold text-gray-900 text-base">
                <span>Total Due Today</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          )}

          <div className="pt-4 flex space-x-3 border-t border-gray-100 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || success}
              className={`flex-1 py-3 rounded-lg text-white font-medium transition-colors ${
                isLoading || success ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isLoading ? "Redirecting..." : "Proceed to Checkout"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BuyScanGunModal;
